const ANALYTICS_STORAGE_KEY = "bob.analytics.session";
const ANALYTICS_ENDPOINT = `${process.env.REACT_APP_BACKEND_URL || ""}/api/analytics/event`;

export const TRACKED_EVENT_TYPES = new Set([
  "page_view",
  "home_view",
  "store_view",
  "product_detail_view",
  "shein_view",
  "country_page_view",
  "shipping_rates_view",
  "shipping_only_view",
  "warehouses_view",
  "login_view",
  "signup_view",
  "product_view",
  "product_search",
  "product_filter",
  "product_sort",
  "product_add_to_cart_click",
  "product_add_to_cart_success",
  "product_add_to_cart_failed",
  "cart_view",
  "checkout_start",
  "checkout_submit_attempt",
  "checkout_success",
  "checkout_failed",
  "shipping_calculator_view",
  "shipping_calculator_calculate",
  "shipping_country_select",
  "shipping_method_select",
  "shipping_only_open",
  "warehouse_copy_address",
  "shipping_mark_copy",
  "whatsapp_click",
  "contact_click",
  "shop_and_ship_click",
  "bob_store_click",
  "china_suppliers_section_view",
  "china_suppliers_open_click",
  "customer_login_success",
  "customer_login_failed",
  "customer_signup_success",
  "customer_signup_failed",
  "email_verification_requested",
  "email_verification_success",
  "password_reset_requested",
]);

const MAX_STRING_LENGTH = 180;
const MAX_QUERY_LENGTH = 80;
const MAX_METADATA_DEPTH = 2;
const MAX_METADATA_KEYS = 12;

function safeString(value, maxLength = MAX_STRING_LENGTH) {
  if (typeof value !== "string") return "";
  return value.trim().replace(/\s+/g, " ").slice(0, maxLength);
}

function safeNumber(value) {
  if (typeof value !== "number" || !Number.isFinite(value)) return undefined;
  return Number(value);
}

function safeBoolean(value) {
  return typeof value === "boolean" ? value : undefined;
}

function sanitizeMetadata(value, depth = 0) {
  if (depth > MAX_METADATA_DEPTH || value == null) return undefined;
  if (typeof value === "string") return safeString(value);
  if (typeof value === "number") return safeNumber(value);
  if (typeof value === "boolean") return value;
  if (Array.isArray(value)) {
    const cleaned = value
      .slice(0, 8)
      .map((item) => sanitizeMetadata(item, depth + 1))
      .filter((item) => item !== undefined);
    return cleaned.length ? cleaned : undefined;
  }
  if (typeof value === "object") {
    const entries = Object.entries(value)
      .slice(0, MAX_METADATA_KEYS)
      .map(([key, item]) => [safeString(key, 40), sanitizeMetadata(item, depth + 1)])
      .filter(([key, item]) => key && item !== undefined);
    if (!entries.length) return undefined;
    return Object.fromEntries(entries);
  }
  return undefined;
}

function sanitizeProduct(product) {
  if (!product || typeof product !== "object") return undefined;
  const cleaned = {
    id: safeString(product.id || product.product_id || product.productId, 60),
    sku: safeString(product.sku, 60),
    title: safeString(product.title || product.name, 120),
    category: safeString(product.category, 60),
    shein_category: safeString(product.shein_category || product.sheinCategory, 60),
  };
  return Object.values(cleaned).some(Boolean) ? cleaned : undefined;
}

function sanitizeCountry(country) {
  if (!country || typeof country !== "object") return undefined;
  const cleaned = {
    code: safeString(country.code || country.country_code || country.countryCode, 12).toUpperCase(),
    name: safeString(country.name || country.country_name || country.countryName, 80),
  };
  return Object.values(cleaned).some(Boolean) ? cleaned : undefined;
}

function sanitizeShipping(shipping) {
  if (!shipping || typeof shipping !== "object") return undefined;
  const cleaned = {
    method: safeString(shipping.method || shipping.method_id || shipping.methodId, 40),
    category: safeString(shipping.category || shipping.category_id || shipping.categoryId, 60),
    unit: safeString(shipping.unit, 16),
    quantity: safeNumber(shipping.quantity),
    secondary_quantity: safeNumber(shipping.secondary_quantity || shipping.secondaryQuantity),
    invoice_value: safeNumber(shipping.invoice_value || shipping.invoiceValue),
    total: safeNumber(shipping.total),
  };
  return Object.values(cleaned).some((value) => value !== undefined && value !== "") ? cleaned : undefined;
}

function getAnonymousSessionId() {
  if (typeof window === "undefined") return "";
  try {
    const existing = window.localStorage.getItem(ANALYTICS_STORAGE_KEY);
    if (existing) return existing;
    const created =
      typeof window.crypto?.randomUUID === "function"
        ? window.crypto.randomUUID()
        : `session-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    window.localStorage.setItem(ANALYTICS_STORAGE_KEY, created);
    return created;
  } catch {
    return `session-${Date.now()}`;
  }
}

function getDeviceClass() {
  if (typeof window === "undefined") return "";
  const width = window.innerWidth || 0;
  if (width && width < 640) return "mobile";
  if (width && width < 1024) return "tablet";
  return "desktop";
}

function buildBasePayload() {
  if (typeof window === "undefined") return {};
  return {
    page_path: safeString(`${window.location.pathname}${window.location.search}`, 220),
    source: "website",
    language: safeString(document.documentElement.getAttribute("lang") || "en", 12),
    theme: safeString(document.documentElement.dataset.theme || "light", 16),
    anonymous_session_id: safeString(getAnonymousSessionId(), 80),
    referrer: safeString(document.referrer, 220),
    device_class: getDeviceClass(),
  };
}

function toEventPayload(eventType, payload = {}) {
  if (!TRACKED_EVENT_TYPES.has(eventType)) return null;
  const base = buildBasePayload();
  const searchQuery = safeString(payload.search_query || payload.searchQuery, MAX_QUERY_LENGTH);

  const eventPayload = {
    event_type: eventType,
    page_path: safeString(payload.page_path || payload.pagePath, 220) || base.page_path,
    source: "website",
    route_name: safeString(payload.route_name || payload.routeName, 80),
    language: safeString(payload.language, 12) || base.language,
    theme: safeString(payload.theme, 16) || base.theme,
    anonymous_session_id: safeString(payload.anonymous_session_id || payload.anonymousSessionId, 80) || base.anonymous_session_id,
    customer_id: safeString(payload.customer_id || payload.customerId, 40),
    device_class: safeString(payload.device_class || payload.deviceClass, 16) || base.device_class,
    referrer: safeString(payload.referrer, 220) || base.referrer,
    product: sanitizeProduct(payload.product),
    country: sanitizeCountry(payload.country),
    shipping: sanitizeShipping(payload.shipping),
    search_query: searchQuery || undefined,
    metadata: sanitizeMetadata({ source: "website", ...(payload.metadata || {}) }),
  };

  if (!eventPayload.page_path) return null;
  return eventPayload;
}

export function trackEvent(eventType, payload = {}) {
  if (typeof window === "undefined") return;
  const eventPayload = toEventPayload(eventType, payload);
  if (!eventPayload) return;

  try {
    const body = JSON.stringify(eventPayload);
    if (body.length > 8_192) {
      eventPayload.metadata = sanitizeMetadata({ truncated: true, reason: "payload_too_large" });
    }
    const finalBody = JSON.stringify(eventPayload);
    if (navigator.sendBeacon) {
      const blob = new Blob([finalBody], { type: "application/json" });
      navigator.sendBeacon(ANALYTICS_ENDPOINT, blob);
      return;
    }
    fetch(ANALYTICS_ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: finalBody,
      keepalive: true,
      credentials: "omit",
    }).catch(() => {});
  } catch {
    // Tracking must never block customer flows.
  }
}

